import { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { imageUrl, invalidateBranding } from "../utils/branding";
import { notifyProfileChanged, useProfile } from "./useProfile";

const refreshTried = new Set();

/**
 * The active company (the one from the session) with its basic info and logo, in order: company, info, logo.
 * ready = all of that has arrived (or is definitively unavailable), so the dashboard may load the rest.
 */
export function useActiveCompany() {
  const { companies, settled, error } = useProfile();
  const company = companies.find((f) => f.current) || companies[0] || null;

  // Data from the ini table has not been fetched yet: fetch it automatically once per company.
  const [infoDone, setInfoDone] = useState(false);
  useEffect(() => {
    if (!settled) return;
    if (!company || company.info || refreshTried.has(company.id)) {
      setInfoDone(true);
      return;
    }
    refreshTried.add(company.id);
    let alive = true;
    apiFetch(`/company_info_refresh.php?id=${company.id}`, { method: "POST" })
      .then((r) => r.json())
      .then((r) => {
        if (r?.success) {
          invalidateBranding();
          notifyProfileChanged();
        }
      })
      .catch(() => {})
      .finally(() => alive && setInfoDone(true));
    return () => {
      alive = false;
    };
  }, [settled, company?.id, Boolean(company?.info)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Logo: wait for it to load (or fail), so it does not appear only after the rest of the page.
  const logoSrc = company?.has_logo ? imageUrl("company", company.id, company.logo_v) : null;
  const [logoDoneFor, setLogoDoneFor] = useState(null);
  useEffect(() => {
    if (!logoSrc) return;
    const img = new Image();
    const done = () => setLogoDoneFor(logoSrc);
    img.onload = done;
    img.onerror = done;
    img.src = logoSrc;
  }, [logoSrc]);

  const ready = settled && infoDone && (!logoSrc || logoDoneFor === logoSrc);
  return { company, info: company?.info || null, logoSrc, ready, error };
}
